import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  type ImageryImportTarget,
  type ImageryOptionKind,
  useImageryOptionsByCoords,
  useImportImagery,
} from "../../../../hooks/use-imagery";
import { ApiError } from "../../../../lib/api";
import { Button } from "../../../ui/button";
import { ImageryImportMenu } from "./imagery-import-menu";
import { ImageryOptionsSkeleton } from "./imagery-options-skeleton";

interface Props {
  recordId: string | undefined;
  latitude: number | null;
  longitude: number | null;
}

export function ImageryOptionsPanel({ recordId, latitude, longitude }: Props): React.ReactElement {
  const hasCoordinates = latitude != null && longitude != null;
  const { data, isLoading, isFetching, isError, error, refetch } = useImageryOptionsByCoords(latitude, longitude);
  const importImagery = useImportImagery(recordId);

  const importDisabled = !recordId;
  const importHint = importDisabled ? "Save the record first" : undefined;

  const handleRefresh = (): void => {
    refetch();
  };

  const handleImport = (kind: ImageryOptionKind, target: ImageryImportTarget): void => {
    if (!latitude || !longitude) return;
    importImagery.mutate(
      { kind, target, lat: latitude, lng: longitude },
      {
        onSuccess: () => toast.success("Image imported"),
        onError: (err) => {
          if (err instanceof ApiError && err.code === "PHOTO_LIMIT") {
            toast.error("Maximum 8 photos allowed");
          } else {
            toast.error(err instanceof Error ? err.message : "Failed to import image");
          }
        },
      },
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-muted-foreground">Imagery For This Address</p>
        {hasCoordinates && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isFetching}
            onClick={handleRefresh}
            data-testid="imagery-refresh-button"
          >
            {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </Button>
        )}
      </div>

      {!hasCoordinates && (
        <p className="text-xs text-muted-foreground mb-2" data-testid="imagery-no-coordinates-hint">
          Select an address with the lookup first.
        </p>
      )}

      {hasCoordinates && isLoading && <ImageryOptionsSkeleton />}

      {hasCoordinates && isError && !isLoading && (
        <p className="text-sm text-destructive" data-testid="imagery-fetch-error">
          {error instanceof Error ? error.message : "Failed to fetch imagery"}
        </p>
      )}

      {hasCoordinates && data && (
        <>
          {importHint && (
            <p className="text-xs text-muted-foreground mb-2" data-testid="imagery-import-hint">
              {importHint}
            </p>
          )}
          {!data.floodAvailable && (
            <p className="text-xs text-muted-foreground mb-2" data-testid="imagery-flood-unavailable">
              Flood hazard mapping is not available for this region ({data.region}).
            </p>
          )}
          {data.options.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4 border rounded-md">
              No imagery candidates found.
            </p>
          ) : (
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {data.options.map((option) => (
                <div key={option.kind} className="rounded-md border" data-testid={`imagery-option-${option.kind}`}>
                  <div className="aspect-square bg-muted overflow-hidden rounded-t-md">
                    <img src={option.url} alt={option.label} className="h-full w-full object-cover" />
                  </div>
                  <div className="p-2 space-y-1">
                    <p className="text-xs font-medium truncate">{option.label}</p>
                    <p className="text-xs text-muted-foreground truncate">{option.attribution}</p>
                    <ImageryImportMenu
                      optionKind={option.kind}
                      disabled={importDisabled || importImagery.isPending}
                      onImport={(target) => handleImport(option.kind, target)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
